<template>
  <div class="container">
    <div class="main-card">
      <div class="inner-box mb-4">
        <div class="section-header-row">
          <h4 class="section-title">1. Item Identity</h4>
          
          <div class="d-flex align-items-center gap-2">
            <span :class="itemDetails.isReturned ? 'text-primary' : 'text-muted'" class="fw-bold status-label">
              {{ itemDetails.isReturned ? 'RETURNABLE' : 'NON-RETURNABLE' }}
            </span>
            <label class="switch">
              <input type="checkbox" v-model="itemDetails.isReturned">
              <span class="slider round"></span>
            </label>
          </div>
        </div>

        <div class="profile-layout mt-3">
          <div class="image-profile-section">
            <div class="image-preview-container">
              <img 
                :src="itemDetails.itemImageBase64 ? 'data:image/png;base64,' + itemDetails.itemImageBase64 : defaultPlaceholder" 
                alt="Item Preview" 
                class="profile-img"
              />
              <div class="image-overlay">
                <div class="action-button-row">
                  <label for="itemImage" class="overlay-btn" title="Upload Image">
                    <i class="fa fa-upload"></i>
                  </label>
                  <button v-if="itemDetails.itemImageBase64" @click="removeImage" class="overlay-btn remove-btn" title="Remove Image">
                    <i class="fa fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
            <input type="file" id="itemImage" @change="handleImageUpload" accept="image/*" class="d-none">
            <p class="text-muted small mt-2">Item Image</p>
          </div>

          <div class="details-form-section">
            <div class="form-grid">
              <div class="form-group full-width">
                <label>Item Name<span class="text-danger">*</span></label>
                <input type="text" v-model="itemDetails.itemName" class="form-control" placeholder="Enter Item Name" required>
              </div>

              <div class="form-group">
                <label>SAP Code<span class="text-danger">*</span></label>
                <input type="text" v-model="itemDetails.sapCode" class="form-control" placeholder="Enter SAP Code" required>
              </div>

              <div class="form-group">
                <label>Item Type<span class="text-danger">*</span></label>
                <select v-model="itemDetails.itemType" class="form-control" required>
                  <option disabled value="">Select Item Type</option>
                  <option v-for="type in ItemTypeList" :key="type.id" :value="type.id">{{ type.name }}</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="inner-box">
        <div class="section-header-row">
          <h4 class="section-title mb-0">2. Manage Variants</h4>
          <button class="btn btn-primary btn-sm m-0" @click="openAddVariantModal">Add Variant</button>
        </div>
        
        <div class="table-responsive mt-3">
          <table v-if="variants.length > 0" class="table">
            <thead>
              <tr>
                <th style="width: 50px;">#</th>
                <th>Variant Name</th>
                <th class="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(variant, index) in variants" :key="index">
                <td>{{ index + 1 }}</td>
                <td>{{ variant.name }}</td>
                <td class="text-center">
                  <i class="fa fa-edit text-primary me-3 action-icon" title="Edit" @click="openEditVariantModal(index)"></i>
                  <i class="fa fa-trash text-danger action-icon" title="Delete" @click="openDeleteConfirmModal(index)"></i>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="text-center py-3 text-muted">
            No variants added yet. Click "Add Variant" to create one.
          </div>
        </div>
      </div>

      <div class="form-actions">
        <button class="btn btn-danger" @click="goBack" :disabled="loading">Cancel</button>
        <button class="btn btn-success ms-2" @click="saveForm" :disabled="loading">
          <span v-if="loading"><i class="fa fa-spinner fa-spin"></i> Saving...</span>
          <span v-else>Save Item</span>
        </button>
      </div>
    </div>

    <div v-if="showAddVariantModal || showEditVariantModal" class="custom-modal-overlay">
      <div class="custom-modal">
        <h5 class="mb-3">{{ showEditVariantModal ? 'Edit Variant' : 'Add Variant' }}</h5>
        <div class="mb-3">
          <label class="mb-1">Variant Name<span class="text-danger">*</span></label>
          <input type="text" class="form-control" v-model="currentVariantName" @keyup.enter="saveVariant">
        </div>
        <div class="d-flex justify-content-end gap-2 mt-4">
          <button class="btn btn-secondary btn-sm" @click="closeVariantModal">Cancel</button>
          <button class="btn btn-primary btn-sm" @click="saveVariant">{{ showEditVariantModal ? 'Update' : 'Add' }}</button>
        </div>
      </div>
    </div>

    <div v-if="showDeleteConfirmModal" class="custom-modal-overlay">
      <div class="custom-modal">
        <h5 class="text-danger mb-3">Confirm Delete</h5>
        <p>Are you sure you want to delete <strong>{{ variantToDelete?.name }}</strong>?</p>
        <div class="d-flex justify-content-end gap-2 mt-4">
          <button class="btn btn-secondary btn-sm" @click="showDeleteConfirmModal = false">Cancel</button>
          <button class="btn btn-danger btn-sm" @click="deleteVariant">Delete</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchItemType, createNewItem } from '@/services/dataService';
import { useToast } from "vue-toastification";

export default {
  name: "CreateItemForm",
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      token: localStorage.getItem('token_set') || '',
      loading: false,
      ItemTypeList: [],
      defaultPlaceholder: 'https://cdn-icons-png.flaticon.com/512/1160/1160358.png',
      itemDetails: {
        itemName: '',
        itemType: '',
        sapCode: '',
        itemImageBase64: null,
        file: null,
        isReturned: false
      },
      variants: [],
      showAddVariantModal: false,
      showEditVariantModal: false,
      showDeleteConfirmModal: false,
      currentVariantName: '',
      editingVariantIndex: null,
      variantToDelete: null
    };
  },
  created() {
    this.loadItemType();
  },
  methods: {
    async loadItemType() {
      try {
        const response = await fetchItemType(this.token);
        this.ItemTypeList = response.data.data || [];
      } catch (error) {
        this.toast.error('Error loading Item Types.');
      }
    },
    handleImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        this.toast.error("Invalid file type.");
        event.target.value = "";
        return;
      }

      const maxSizeInBytes = 1024 * 1024; 
      if (file.size > maxSizeInBytes) {
        this.toast.error("File is too large. Max 1MB.");
        event.target.value = "";
        return;
      }

      this.itemDetails.file = file;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.itemDetails.itemImageBase64 = e.target.result.split(',')[1];
      };
      reader.readAsDataURL(file);
    },
    removeImage() {
      this.itemDetails.file = null;
      this.itemDetails.itemImageBase64 = null;
      const input = document.getElementById('itemImage');
      if(input) input.value = "";
    },
    openAddVariantModal() {
      this.currentVariantName = '';
      this.showAddVariantModal = true;
    },
    openEditVariantModal(index) {
      this.editingVariantIndex = index;
      this.currentVariantName = this.variants[index].name;
      this.showEditVariantModal = true;
    },
    closeVariantModal() {
      this.showAddVariantModal = false;
      this.showEditVariantModal = false;
    },
    saveVariant() {
      if (!this.currentVariantName.trim()) return;
      if (this.showEditVariantModal) {
        this.variants[this.editingVariantIndex].name = this.currentVariantName;
      } else {
        this.variants.push({ name: this.currentVariantName });
      }
      this.closeVariantModal();
    },
    openDeleteConfirmModal(index) {
      this.variantToDelete = { ...this.variants[index], index };
      this.showDeleteConfirmModal = true;
    },
    deleteVariant() {
      this.variants.splice(this.variantToDelete.index, 1);
      this.showDeleteConfirmModal = false;
    },
    goBack() {
      this.$router.push('/items');
    },
    async saveForm() {
      if (!this.itemDetails.itemName || !this.itemDetails.itemType || !this.itemDetails.sapCode) {
        this.toast.error("Please fill in all required fields.");
        return;
      }

      this.loading = true;
      const payload = {
        item_name: this.itemDetails.itemName,
        sap_code: this.itemDetails.sapCode,
        item_type_id: parseInt(this.itemDetails.itemType),
        item_img: this.itemDetails.itemImageBase64,
        is_returned: this.itemDetails.isReturned ? "1" : "0",
        variants: this.variants.map(v => v.name)
      };

      try {
        const response = await createNewItem(this.token, payload);
        if (response.data.status === 'success') {
          this.toast.success("Item created successfully!");
          this.goBack();
        }
      } catch (error) {
        this.toast.error("An error occurred while saving.");
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.container { padding: 10px; max-width: 1100px; margin: auto; }
.main-card { background: #fff; border-radius: 8px; padding: 25px; }
.inner-box { border: 1px solid #f0f0f0; background: #fafafa; padding: 20px; border-radius: 6px; }
.section-title { font-size: 15px; font-weight: 700; color: #333; text-transform: uppercase; margin-bottom: 10px; }

/* Switch Logic */
.switch { position: relative; display: inline-block; width: 44px; height: 22px; }
.switch input { opacity: 0; width: 0; height: 0; }
.slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #ccc; transition: .4s; }
.slider:before { position: absolute; content: ""; height: 16px; width: 16px; left: 3px; bottom: 3px; background-color: white; transition: .4s; }
input:checked + .slider { background-color: #28a745; }
input:checked + .slider:before { transform: translateX(22px); }
.slider.round { border-radius: 34px; }
.slider.round:before { border-radius: 50%; }

.status-label { font-size: 12px; }

/* Profile Styles */
.profile-layout { display: flex; gap: 35px; }
.image-profile-section { display: flex; flex-direction: column; align-items: center; width: 140px; }
.image-preview-container { position: relative; width: 130px; height: 130px; border-radius: 12px; overflow: hidden; border: 2px solid #ddd; background: #fff; }
.profile-img { width: 100%; height: 100%; object-fit: contain; }
.image-overlay { position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.3); display: flex; justify-content: center; align-items: center; opacity: 0; transition: 0.3s; }
.image-preview-container:hover .image-overlay { opacity: 1; }
.action-button-row { display: flex; gap: 12px; }
.overlay-btn { background: #fff; width: 34px; height: 34px; border-radius: 50%; display: flex; justify-content: center; align-items: center; cursor: pointer; border: none; color: #333; transition: 0.2s; }
.remove-btn:hover { background: #dc3545; color: #fff; }

.details-form-section { flex: 1; }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
.full-width { grid-column: 1 / -1; }
.form-group label { display: block; font-weight: 600; margin-bottom: 6px; font-size: 13px; color: #555; }
.form-control { width: 100%; padding: 10px; border: 1px solid #ccc; border-radius: 4px; }

.section-header-row { display: flex; justify-content: space-between; align-items: center; }
.form-actions { display: flex; justify-content: flex-end; margin-top: 30px; }
.table { width: 100%; border-collapse: collapse; background: #fff; font-size: 14px; }
.table th, .table td { padding: 12px; border: 1px solid #eee; }
.table th { background: #f8f9fa; }
.action-icon { cursor: pointer; font-size: 1.1rem; }
.d-none { display: none; }

.custom-modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); display: flex; justify-content: center; align-items: center; z-index: 1000; }
.custom-modal { background: #fff; padding: 25px; border-radius: 8px; width: 420px; box-shadow: 0 5px 15px rgba(0,0,0,0.3); }
</style>